<template lang="pug">
#shortcut.w-full.h-full.items-center
  .side-menu(class="mt-[0.375rem] xl:mt-[0.48rem]")
    BaseMenuFrame
      ul.menu-container.w-full
        .pt-1.rounded-sm
          .grid.grid-cols-2.gap-x-1.px-1
            button.menu-item.w-full.mb-1.rounded-lg.text-start.px-1.bg-linear-to-l.from-menu-active-from.to-menu-active-to(v-for="(item, index) in filterShortcutList", :key="`shortcut-${index}`", :disabled="item.disabled", :class="[itemStyle(index), item.disabled ? 'disabled-nav' : '']", @click="showPage(item)")
              .flex.items-center.gap-x-1.w-full.h-6(class="portrait:h-10")
                .w-4.h-4.overflow-hidden(class="portrait:h-8 portrait:w-8")
                  img.w-full(:src="item.iconUrl", alt="item.item", loading="lazy")
                .flex-1.overflow-hidden
                  p.w-full.leading-none.truncate.text-primary-text(:class="textStyle(index)") {{ item.name }}
</template>

<script setup>
// 預先導入所有常用圖示
import defaultIcon from 'assets/images/side/idxcategory_icon_default_both.png'
import depositIcon from 'assets/images/side/idxcategory_icon_deposit_normal.png'
import faqIcon from 'assets/images/side/idxcategory_icon_faq_normal.png'
import friendsIcon from 'assets/images/side/idxcategory_icon_friends_normal.png'
import mybetIcon from 'assets/images/side/idxcategory_icon_mybet_normal.png'
import promoCodeIcon from 'assets/images/side/idxcategory_icon_promocode_normal.png'
import promotionIcon from 'assets/images/side/idxcategory_icon_promotion_normal.png'
import tournamentIcon from 'assets/images/side/idxcategory_icon_tournament_normal.png'
import transectionIcon from 'assets/images/side/idxcategory_icon_transection_normal.png'
import vipcardIcon from 'assets/images/side/idxcategory_icon_vipcard_normal.png'
import { computed } from 'vue'

import { useNuxtApp } from '#app'
import { useI18n } from '#imports'
import { useLayout } from '@/composables/core/useLayout'
import { useSite } from '@/composables/core/useSite'
import { useMainStore } from '@/stores/main'

const { $SoundControl, $analytics, $openWithIframeFallback } = useNuxtApp()
const { t, locale } = useI18n()
const sideMenu = useSideMenu()
const { setRoom, playGame } = useGame()
const { activityList } = useActivity('shortcut')
const store = useMainStore()
const { setModal, setModalParams } = store
const { isGuest } = useAuthToken()
const { poCurrencyEnable, promoCodeEnable } = useSite()
const { quickLayout } = useLayout()
const { checkGameRestriction } = useGameRestriction()
const { verify } = useVerify()
const { getCdnUrl } = useCdnAssets()

const moreThanFive = computed(() => filterShortcutList.value.length > 5)

const hasVipCard = computed(() => activityList.value?.find((item) => item.template === 'Template_vip_card_pro'))

const hasTournament = computed(() => activityList.value?.find((item) => item.tournament === 1))

const hasReferralFriends = computed(() => activityList.value?.find((item) => item.template === 'Template_invite_friends'))

const filterShortcutList = computed(() => {
  const filterList = formattedShortcutList.value?.filter((item) => item.display)
  return filterList
})

const formattedShortcutList = computed(() => {
  const formattedList = quickLayout.value.map((element) => {
    let name = element.nameI18n?.[locale.value] ? element.nameI18n[locale.value] : element.nameI18n?.['en-US']
    const iconUrl = shortcutIcon(element)
    let display = true
    let disabled = false
    let action = { type: '', target: '', needAuth: true }
    let eventName = null
    switch (element.item) {
      case 'Tournament':
        if (!hasTournament.value) {
          display = false
        }
        const tournamentId = activityList.value?.find((item) => item.classification === 'tournament')?.activityNo
        name = name ? name : t('common.tournament')
        action = { type: 'modal', target: 'ModalMission', id: tournamentId, needAuth: true }
        eventName = 'Tournament'
        break
      case 'Friends':
        // 當沒有指定活動時 顯示但不可點擊
        disabled = !hasReferralFriends.value
        name = name ? name : t('common.friends')
        action = { type: 'modal', target: 'ModalFriends', needAuth: true }
        eventName = 'Friends'
        break
      case 'Promo Code':
        disabled = !promoCodeEnable.value
        name = name ? name : t('common.promocode')
        action = { type: 'modal', target: 'ModalMission', id: 'promocode', needAuth: true }
        eventName = 'PromoCode'
        break
      case 'VIP Card':
        if (!hasVipCard.value) {
          display = false
        }
        const vipcardId = activityList.value?.find((item) => item.template === 'Template_vip_card_pro')?.activityNo
        action = { type: 'modal', target: `VipCard`, id: vipcardId, needAuth: true }
        name = name ? name : t('common.vip_card')
        eventName = 'VipCard'
        break
      case 'Promotions':
        const promotionId = activityList.value?.find((item) => item.classification === 'promotion')?.activityNo
        name = name ? name : t('common.promotion')
        action = { type: 'modal', target: 'ModalMission', id: promotionId, needAuth: false }
        eventName = 'Promotions'
        break
      case 'Deposit':
        if (poCurrencyEnable.value) {
          display = false
        }
        name = name ? name : t('common.deposit')
        action = { type: 'modal', target: 'ModalDepositProxy', needAuth: true }
        eventName = 'Deposit'
        break
      case 'Transaction':
        name = name ? name : t('common.transaction')
        action = { type: 'modal', target: 'ModalDepositProxy', modal: 'transaction', needAuth: true }
        eventName = 'Transaction'
        break
      case 'My Bets':
        name = name ? name : t('common.history')
        action = { type: 'modal', target: 'ModalStatement', needAuth: true }
        eventName = 'History'
        break
      case 'FAQ':
        name = name ? name : t('common.faq')
        action = { type: 'modal', target: 'ModalFAQ', needAuth: false }
        eventName = 'FAQ'
        break
      default:
        action = { type: 'link', target: element.externalLink, needAuth: false }
        eventName = element.item
        break
    }

    return { ...element, display, disabled, name, iconUrl, action, eventName }
  })

  return formattedList
})

// 圖示映射表
const sideIconsMap = {
  default: defaultIcon,
  deposit: depositIcon,
  faq: faqIcon,
  friends: friendsIcon,
  mybet: mybetIcon,
  promocode: promoCodeIcon,
  promotion: promotionIcon,
  tournament: tournamentIcon,
  transection: transectionIcon,
  vipcard: vipcardIcon,
}

const shortcutIcon = (element) => {
  try {
    // 優先使用 API 返回的圖示 URL
    if (element.iconUrl?.[0]?.url) {
      return getCdnUrl(element.iconUrl?.[0]?.url)
    }

    // 否則使用本地圖示
    if (!element.item) {
      return defaultIcon
    }

    let item = element.item.replace(' ', '').toLowerCase()

    // 處理特殊命名映射
    const nameMapping = {
      promotions: 'promotion',
      transaction: 'transection',
      mybets: 'mybet',
      vipcard: 'vipcard',
    }

    item = nameMapping[item] || item

    // 返回對應的圖示，若找不到則返回預設圖示
    return sideIconsMap[item] || defaultIcon
  } catch (error) {
    console.error('Failed to load shortcut icon:', error, element)
    return defaultIcon
  }
}

const itemStyle = (index) => {
  if (moreThanFive.value) {
    const gapNumber = filterShortcutList.value.length - 5
    return index + 1 > 5 - gapNumber ? 'col-span-1' : 'col-span-2'
  } else {
    return 'col-span-2'
  }
}

const textStyle = (index) => {
  if (moreThanFive.value) {
    const gapNumber = filterShortcutList.value.length - 5
    return index + 1 > 5 - gapNumber ? 'text-xs' : 'text-sm'
  } else {
    return 'text-xs'
  }
}

const play = (item) => {
  $SoundControl.sound.play('click')
  verify('ENTER_GAME').then((isVerified) => {
    if (isVerified) {
      $analytics.push({
        event: 'game_click',
        location: store.getTrackLocation,
        locationId: null,
        provider: item.providerId,
        gameId: item.gameId,
        gameName: item.name,
      })
      playGame({ game: item, locale: locale.value, entry: 'Shortcut' })
    }
  })
}

const showPage = (item) => {
  $SoundControl.sound.play('click')
  const { action, externalLink } = item
  const { type, target, id, needAuth, modal } = action
  if (needAuth && isGuest.value) {
    setModal('ModalGuestLogin')
  } else {
    if (type === 'modal') {
      if (id) {
        setModalParams({ id })
        setModal(target)
      } else if (modal) {
        setModalParams({ modal })
        setModal(target)
      } else {
        setModal(target)
      }
    } else if (type === 'game') {
      if (checkGameRestriction(item, 'Shortcut')) {
        return
      }
      play(item)
    } else if (type === 'category') {
      sideMenu.restoreMenuItemStack()
      sideMenu.selectMenuItem(item)
      setRoom(item)
    } else if (type === 'link' && externalLink) {
      $openWithIframeFallback(item.externalLink)
    }
  }
}
</script>

<style scoped>
@reference "@/assets/css/tailwind.css";

#shortcut {
  @apply w-full h-full items-center;
  transition: all 0.3s ease;
}

.side-menu {
  width: 16.25rem;
  max-width: 100%;
  transition: all 0.3s ease;
}

@media (orientation: portrait) {
  .side-menu {
    @apply px-4 w-dvw;
  }
}

.menu-container {
  max-height: 60vh;
  height: 100%;
  border-radius: 4px;
  position: relative;
  z-index: 1;
}

.disabled-nav {
  filter: grayscale(100%);
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
